import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { contactAPI } from '../services/api';
import API_BASE_URL from '../config/api';
import LoadingScreen from '../components/LoadingScreen';
import Toast from '../components/Toast';
import { useSEO } from '../utils/useSEO';
import { useLang } from '../context/LanguageContext';
import { Helmet } from 'react-helmet-async';
import '../styles/Contact.css';

const Contact = () => {
  useSEO('contact');
  const { nl } = useLang();
  const L = (obj, field) => (nl && obj?.[field + '_nl']) || obj?.[field] || '';
  const [contactData, setContactData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', company: '', service: '', message: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [seoData, setSeoData] = useState(null);
  
  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ num1, num2, answer: num1 + num2 });
    setCaptchaAnswer('');
  };
  
  useEffect(() => {
  fetch(`${API_BASE_URL}/api/seo/contact`)
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        setSeoData(data.data);
      }
    })
    .catch(err => console.error("SEO fetch error:", err));
}, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const contactResponse = await axios.get(`${API_BASE_URL}/api/contactpage`);
        setContactData(contactResponse.data.data);
        setLocations(contactResponse.data.data.addresses?.filter(addr => addr.isActive).sort((a, b) => a.order - b.order) || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({ firstName: true, lastName: true, email: true });
    const e1 = validateField('firstName', formData.firstName);
    const e2 = validateField('lastName', formData.lastName);
    const e3 = validateField('email', formData.email);
    const allErrors = { ...e1, ...e2, ...e3 };
    if (Object.keys(allErrors).length > 0) return;
    
    if (parseInt(captchaAnswer) !== captchaQuestion.answer) {
      alert(nl ? 'Ongeldige CAPTCHA' : 'Invalid CAPTCHA');
      generateCaptcha();
      return;
    }
    
    setSubmitting(true);
    try {
      await contactAPI.submit(formData);
      setShowToast(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', company: '', service: '', message: '' });
      generateCaptcha();
    } catch (error) {
      console.error('Error:', error);
      alert('Error sending message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const validateName = (name) => /^[a-zA-Z\s'-]{2,}$/.test(name.trim());
  const validateEmail = (email) => /^[a-zA-Z][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  const validateField = (field, value) => {
    const newErrors = { ...fieldErrors };
    switch (field) {
      case 'firstName':
        if (!value.trim()) newErrors.firstName = nl ? 'Voornaam is verplicht' : 'First name is required';
        else if (!validateName(value)) newErrors.firstName = nl ? 'Alleen letters, minimaal 2 tekens' : 'Only letters, minimum 2 characters';
        else delete newErrors.firstName;
        break;
      case 'lastName':
        if (!value.trim()) newErrors.lastName = nl ? 'Achternaam is verplicht' : 'Last name is required';
        else if (!validateName(value)) newErrors.lastName = nl ? 'Alleen letters, minimaal 2 tekens' : 'Only letters, minimum 2 characters';
        else delete newErrors.lastName;
        break;
      case 'email':
        if (!value.trim()) newErrors.email = nl ? 'E-mail is verplicht' : 'Email is required';
        else if (!validateEmail(value)) newErrors.email = nl ? 'Ongeldig e-mailadres (bijv. naam@domein.com)' : 'Invalid email (e.g. name@domain.com)';
        else delete newErrors.email;
        break;
      default:
        break;
    }
    setFieldErrors(newErrors);
    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched[name]) validateField(name, value);
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  if (loading) return <LoadingScreen />;
  if (!contactData) return <div>Error loading contact page</div>;


  console.log('Contact Data:', contactData);

  return (
    <div>
      {showToast && <Toast message="Form submitted successfully!" onClose={() => setShowToast(false)} />}
    

      <Helmet>
        <title>{seoData?.title || 'Aurelia Technology'}</title>
        {seoData?.description && <meta name="description" content={seoData.description} />}
        {seoData?.keywords && <meta name="keywords" content={seoData.keywords} />}
        {seoData?.canonicalUrl && <link rel="canonical" href={seoData.canonicalUrl} />}
        <meta property="og:title" content={seoData?.ogTitle || seoData?.title || 'Aurelia Technology'} />
        <meta property="og:description" content={seoData?.ogDescription || seoData?.description || ''} />
        {seoData?.ogImage && <meta property="og:image" content={seoData.ogImage} />}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData?.twitterTitle || seoData?.ogTitle || seoData?.title || 'Aurelia Technology'} />
        <meta name="twitter:description" content={seoData?.twitterDescription || seoData?.ogDescription || seoData?.description || ''} />
        {(seoData?.twitterImage || seoData?.ogImage) && <meta name="twitter:image" content={seoData.twitterImage || seoData.ogImage} />}
      </Helmet>

      {/* Contact Banner */}
      <section 
        className="contact-banner" 
        style={{
          backgroundImage: contactData.banner?.image ? `url(${contactData.banner.image})` : 'none'
        }}
      >
        <div className="banner-overlay"></div>
        <div className="banner-content">
          <div className="banner-left">
            <h1>{L(contactData.banner, 'title') || 'CONTACT US'}</h1>
            <p>{L(contactData.banner, 'subtitle') || 'We\'re here to help you transform your business with innovative technology solutions'}</p>
          </div>
        </div>
      </section>

      {/* Customer Message */}
      <section className="customer-message-section">
        <div className="customer-message-container">
          <div className="message-layout">
            <div className="message-left">
              <h2>{L(contactData.messageSection, 'title')}</h2>
              <div className="message-divider">
                <div className="divider-line"></div>
                <div className="divider-dots">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
              <p className="message-text">
                {L(contactData.messageSection, 'content') || 'At Highon Innovation, we believe that great partnerships begin with great conversations.'}
              </p>
              <p className="message-cta">
                {nl ? 'Neem vandaag nog contact met ons op en laten we bespreken hoe we uw visie tot leven kunnen brengen.' : 'Get in touch with us today and let\'s discuss how we can bring your vision to life.'}
              </p>
            </div>
            <div className="message-right">
              <img src="/images/contact.png" alt="Contact Us" />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-cta-section" style={{backgroundImage:'url(/images/CTA.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
        <div className="container">
          <div className="contact-header">
            <h2>{L(contactData.ctaSection, 'title') || (nl ? 'Klaar om te beginnen?' : 'Ready to get started?')}</h2>
            <p>{L(contactData.ctaSection, 'subtitle') || (nl ? 'Neem contact op met ons team' : 'Get in touch with our team to discuss how we can help transform your business')}</p>
          </div>
          
          <div className="contact-content" style={{ justifyContent: 'center' }}>
            <div className="contact-form-container" style={{ maxWidth: '800px', width: '100%', margin: '0 auto' }}>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div>
                    <label htmlFor="firstName">{nl ? 'Voornaam' : 'First Name'} *</label>
                    <input 
                      type="text" 
                      id="firstName" 
                      name="firstName" 
                      placeholder={nl ? 'Voornaam' : 'First Name'}
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('firstName')}
                      style={fieldErrors.firstName ? {borderColor: '#dc3545'} : {}}
                      required 
                    />
                    {touched.firstName && fieldErrors.firstName && <span style={{color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block'}}>{fieldErrors.firstName}</span>}
                  </div>
                  <div>
                    <label htmlFor="lastName">{nl ? 'Achternaam' : 'Last Name'} *</label>
                    <input 
                      type="text" 
                      id="lastName" 
                      name="lastName" 
                      placeholder={nl ? 'Achternaam' : 'Last Name'}
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('lastName')}
                      style={fieldErrors.lastName ? {borderColor: '#dc3545'} : {}}
                      required 
                    />
                    {touched.lastName && fieldErrors.lastName && <span style={{color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block'}}>{fieldErrors.lastName}</span>}
                  </div>
                </div>
                <div className="form-row">
                  <div>
                    <label htmlFor="email">{nl ? 'E-mailadres' : 'Email Address'} *</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      placeholder={nl ? 'E-mailadres' : 'Email Address'}
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('email')}
                      style={fieldErrors.email ? {borderColor: '#dc3545'} : {}}
                      required 
                    />
                    {touched.email && fieldErrors.email && <span style={{color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block'}}>{fieldErrors.email}</span>}
                  </div>
                  <div>
                    <label htmlFor="phone">{nl ? 'Telefoonnummer' : 'Phone Number'}</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      name="phone" 
                      placeholder={nl ? 'Telefoonnummer' : 'Phone Number'}
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div>
                    <label htmlFor="company">{nl ? 'Bedrijfsnaam' : 'Company Name'}</label>
                    <input 
                      type="text" 
                      id="company" 
                      name="company" 
                      placeholder={nl ? 'Bedrijfsnaam' : 'Company Name'}
                      value={formData.company}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="service">{nl ? 'Dienst interesse' : 'Service Interest'} *</label>
                    <select 
                      id="service" 
                      name="service" 
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">{nl ? 'Selecteer een dienst' : 'Select a service'}</option>
                      <option value="ai">AI Development</option>
                      <option value="blockchain">Blockchain Development</option>
                      <option value="cloud">Cloud Solutions</option>
                      <option value="mobile">Mobile Development</option>
                      <option value="web">Web Development</option>
                      <option value="bot">Bot Development</option>
                      <option value="consulting">Consulting</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <label htmlFor="message">{nl ? 'Bericht' : 'Message'} *</label>
                <textarea 
                  id="message" 
                  name="message" 
                  placeholder={nl ? 'Vertel ons over uw project' : 'Tell us about your project'} 
                  rows="3"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                ></textarea>
                <div className="captcha-row">
                  <label htmlFor="captcha">{nl ? 'Captcha! Wat is' : 'Captcha ! What is'} {captchaQuestion.num1} + {captchaQuestion.num2}?</label>
                  <input 
                    type="number" 
                    id="captcha" 
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    placeholder="Answer" 
                    required
                  />
                </div>
                <button 
                  type="submit" 
                  className="submit-btn" 
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      {nl ? 'Verzenden...' : 'Sending...'}
                    </>
                  ) : (
                    nl ? 'Bericht verzenden' : 'Send Message'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
